import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '@/api';
import {
  FileText, Layers, Cpu, Upload, ArrowRight, Building2,
  TableProperties, RefreshCw, ClipboardCheck, Map, Shield,
  AlertTriangle, CheckCircle2, Package, ScanLine, Plus, Eye
} from 'lucide-react';

const diStatusColors = {
  processing: 'bg-blue-50 dark:bg-blue-950/50 text-blue-400 border-blue-900',
  review: 'bg-amber-50 dark:bg-amber-950/30 text-primary border-primary/30',
  completed: 'bg-emerald-50 dark:bg-emerald-950/50 text-emerald-400 border-emerald-900',
  synced: 'bg-emerald-50 dark:bg-emerald-950/50 text-emerald-400 border-emerald-900',
  published: 'bg-emerald-50 dark:bg-emerald-950/50 text-emerald-400 border-emerald-900',
  created: 'bg-muted/50 text-muted-foreground border-border',
  uploaded: 'bg-muted/50 text-muted-foreground border-border',
  failed: 'bg-red-50 dark:bg-red-950/50 text-red-400 border-red-900',
};

const actStatusColors = {
  draft: 'bg-muted/50 text-muted-foreground border-border',
  active: 'bg-blue-50 dark:bg-blue-950/50 text-blue-400 border-blue-900',
  in_progress: 'bg-amber-50 dark:bg-amber-950/30 text-amber-400 border-amber-800',
  completed: 'bg-emerald-50 dark:bg-emerald-950/50 text-emerald-400 border-emerald-900',
};

function StatCard({ icon: Icon, label, value, accent, testId }) {
  const accents = {
    orange: 'bg-primary/10 text-primary',
    blue: 'bg-blue-600/10 text-blue-400',
    green: 'bg-emerald-600/10 text-emerald-400',
    purple: 'bg-purple-600/10 text-purple-400',
    amber: 'bg-amber-600/10 text-amber-400',
    default: 'bg-secondary text-muted-foreground',
  };
  return (
    <div className="bg-card border border-border rounded-sm p-5 flex items-start gap-4 hover:border-border/80 transition-colors" data-testid={testId}>
      <div className={`p-2.5 rounded-sm ${accents[accent] || accents.default}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="font-heading font-medium text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
        <p className="font-mono text-2xl font-bold text-foreground mt-0.5">{value}</p>
      </div>
    </div>
  );
}

function MiniStat({ icon: Icon, label, value, color }) {
  return (
    <div className="flex items-center gap-2">
      <div className={`w-2 h-2 rounded-full ${color}`} />
      <span className="font-heading text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
      <span className="font-mono text-sm font-bold text-foreground ml-auto">{value}</span>
    </div>
  );
}

export default function DashboardPage() {
  const [diStats, setDiStats] = useState(null);
  const [actStats, setActStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const navigate = useNavigate();

  const fetchStats = async () => {
    try {
      const [diRes, actRes] = await Promise.all([
        api.get('/stats'),
        api.get('/closeout/stats'),
      ]);
      setDiStats(diRes.data);
      setActStats(actRes.data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const seedData = async () => {
    setSeeding(true);
    try {
      await api.post('/seed');
      await fetchStats();
    } catch (err) {
      console.error('Seed failed:', err);
    } finally {
      setSeeding(false);
    }
  };

  useEffect(() => { fetchStats(); }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <RefreshCw className="w-6 h-6 text-primary animate-spin" />
      </div>
    );
  }

  const diEmpty = !diStats || diStats.total_jobs === 0;
  const actEmpty = !actStats || actStats.total_projects === 0;

  return (
    <div className="p-6 md:p-8 lg:p-10 max-w-[1400px]" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading font-bold text-2xl tracking-tight uppercase text-foreground" data-testid="dashboard-title">Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">FMiQ-One Platform Overview</p>
        </div>
        <div className="flex gap-3">
          {diEmpty && (
            <button onClick={seedData} disabled={seeding} data-testid="seed-data-btn"
              className="flex items-center gap-2 rounded-sm border border-border bg-transparent hover:bg-secondary text-foreground/80 font-heading uppercase tracking-wider text-xs h-10 px-4 transition-colors disabled:opacity-50">
              <Cpu className="w-3.5 h-3.5" />
              {seeding ? 'Seeding...' : 'Load Demo Data'}
            </button>
          )}
          <Link to="/upload" data-testid="new-upload-btn"
            className="flex items-center gap-2 border-l-2 border-primary bg-primary/10 hover:bg-primary/15 text-primary font-heading font-bold uppercase tracking-wider text-sm h-10 px-6 transition-colors">
            <Upload className="w-4 h-4" /> New Upload
          </Link>
        </div>
      </div>

      <div className="space-y-10">
        {/* ═══════════════════════════════════════════════════════════
            DRAWING INTEL SECTION
           ═══════════════════════════════════════════════════════════ */}
        <section data-testid="di-section">
          <div className="flex items-center gap-3 mb-5">
            <div className="p-2 rounded-sm bg-blue-600/10">
              <Layers className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-sm uppercase tracking-wider text-foreground">Build</h2>
              <p className="text-xs text-muted-foreground">AI-powered drawing extraction & digitization</p>
            </div>
            <div className="ml-auto flex gap-2" data-testid="di-quick-actions">
              <Link to="/digitize/upload" className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-heading uppercase tracking-wider text-blue-400 bg-blue-600/10 border border-blue-800/30 rounded-sm hover:bg-blue-600/20 transition-colors">
                <ScanLine className="w-3 h-3" /> Quick Digitize
              </Link>
              <Link to="/upload" className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-heading uppercase tracking-wider text-blue-400 bg-blue-600/10 border border-blue-800/30 rounded-sm hover:bg-blue-600/20 transition-colors">
                <Upload className="w-3 h-3" /> New Job
              </Link>
              <Link to="/jobs" className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-heading uppercase tracking-wider text-muted-foreground border border-border rounded-sm hover:bg-secondary/50 transition-colors">
                <Eye className="w-3 h-3" /> All Jobs
              </Link>
            </div>
          </div>

          {diEmpty ? (
            <div className="bg-card border border-border rounded-sm p-10 text-center">
              <Layers className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm font-heading text-foreground/70">No Drawing Jobs Yet</p>
              <p className="text-xs text-muted-foreground mt-1">Upload drawings or load demo data to get started</p>
            </div>
          ) : (
            <>
              {/* DI Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
                <StatCard icon={FileText} label="Total Jobs" value={diStats.total_jobs} accent="blue" testId="stat-di-jobs" />
                <StatCard icon={Building2} label="Spaces Extracted" value={diStats.total_spaces?.toLocaleString()} accent="green" testId="stat-di-spaces" />
                <StatCard icon={Cpu} label="Asset Candidates" value={diStats.total_assets?.toLocaleString()} accent="orange" testId="stat-di-assets" />
                <StatCard icon={TableProperties} label="Avg Confidence" value={`${Math.round(diStats.avg_confidence * 100)}%`} accent="default" testId="stat-di-confidence" />
              </div>

              {/* DI Status */}
              <div className="grid grid-cols-3 gap-4 mb-5">
                <div className="bg-card border border-border rounded-sm p-4" data-testid="di-processing">
                  <MiniStat icon={null} label="Processing" value={diStats.processing_jobs} color="bg-blue-400 animate-pulse" />
                </div>
                <div className="bg-card border border-border rounded-sm p-4" data-testid="di-review">
                  <MiniStat icon={null} label="Awaiting Review" value={diStats.review_jobs} color="bg-amber-400" />
                </div>
                <div className="bg-card border border-border rounded-sm p-4" data-testid="di-completed">
                  <MiniStat icon={null} label="Completed" value={diStats.completed_jobs} color="bg-emerald-400" />
                </div>
              </div>

              {/* DI Recent Jobs */}
              <div className="bg-card border border-border rounded-sm">
                <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                  <h3 className="font-heading font-bold text-xs uppercase tracking-wider text-foreground/80">Recent Jobs</h3>
                  <Link to="/jobs" className="text-xs text-blue-400 hover:text-blue-300 font-mono flex items-center gap-1 transition-colors" data-testid="view-all-jobs">
                    View All <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
                <div className="divide-y divide-border/50">
                  {diStats.recent_jobs?.slice(0, 5).map(job => (
                    <Link key={job.id} to={`/jobs/${job.id}`} data-testid={`di-job-${job.id}`}
                      className="flex items-center justify-between px-5 py-3 hover:bg-secondary/30 transition-colors group">
                      <div className="flex items-center gap-4 min-w-0">
                        <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                        <div className="min-w-0">
                          <p className="font-mono text-sm text-foreground truncate group-hover:text-primary transition-colors">
                            {job.project_name || job.facility_key || 'Untitled'}
                          </p>
                          <p className="text-[10px] font-mono text-muted-foreground mt-0.5">
                            {job.sheet_count} sheets / {job.space_count} spaces / {job.asset_count} assets
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded text-[10px] font-medium font-mono uppercase tracking-wide border ${diStatusColors[job.status] || diStatusColors.created}`}>
                          {job.status}
                        </span>
                        <ArrowRight className="w-3.5 h-3.5 text-muted-foreground group-hover:text-primary transition-colors" />
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </>
          )}
        </section>

        {/* ═══════════════════════════════════════════════════════════
            ACTIVATE (CONSTRUCTION) SECTION
           ═══════════════════════════════════════════════════════════ */}
        <section data-testid="activate-section">
          <div className="flex items-center gap-3 mb-5">
            <div className="p-2 rounded-sm bg-amber-600/10">
              <ClipboardCheck className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-sm uppercase tracking-wider text-foreground">Activate</h2>
              <p className="text-xs text-muted-foreground">Construction closeout, asset tracking & handoff</p>
            </div>
            <div className="ml-auto flex gap-2" data-testid="act-quick-actions">
              <Link to="/closeout" className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-heading uppercase tracking-wider text-amber-400 bg-amber-600/10 border border-amber-800/30 rounded-sm hover:bg-amber-600/20 transition-colors">
                <Plus className="w-3 h-3" /> New Project
              </Link>
              <Link to="/closeout" className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-heading uppercase tracking-wider text-muted-foreground border border-border rounded-sm hover:bg-secondary/50 transition-colors">
                <Eye className="w-3 h-3" /> All Projects
              </Link>
            </div>
          </div>

          {actEmpty ? (
            <div className="bg-card border border-border rounded-sm p-10 text-center">
              <ClipboardCheck className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm font-heading text-foreground/70">No Activate Projects Yet</p>
              <p className="text-xs text-muted-foreground mt-1">
                Create a project to start tracking construction assets and handoff
              </p>
              <Link to="/closeout" className="inline-flex items-center gap-2 mt-4 px-4 py-2 text-xs font-heading uppercase tracking-wider bg-primary/10 text-primary border border-primary/30 rounded-sm hover:bg-primary/15 transition-colors">
                Go to Activate <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          ) : (
            <>
              {/* Activate Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
                <StatCard icon={Building2} label="Projects" value={actStats.total_projects} accent="amber" testId="stat-act-projects" />
                <StatCard icon={Package} label="Total Assets" value={actStats.total_assets?.toLocaleString()} accent="blue" testId="stat-act-assets" />
                <StatCard icon={Shield} label="Divisions" value={actStats.total_divisions} accent="purple" testId="stat-act-divisions" />
                <StatCard icon={Map} label="Floor Plans" value={actStats.floor_plans_linked} accent="green" testId="stat-act-floorplans" />
              </div>

              {/* Activate Status Row */}
              <div className="grid grid-cols-3 gap-4 mb-5">
                <div className="bg-card border border-border rounded-sm p-4" data-testid="act-verified">
                  <MiniStat label="Verified Assets" value={`${actStats.verified_assets}/${actStats.total_assets}`} color="bg-emerald-400" />
                </div>
                <div className="bg-card border border-border rounded-sm p-4" data-testid="act-completion">
                  <MiniStat label="Avg Completion" value={`${actStats.avg_completion}%`} color="bg-blue-400" />
                </div>
                <div className="bg-card border border-border rounded-sm p-4" data-testid="act-alerts">
                  <MiniStat label="Smart Alerts" value={actStats.total_alerts} color={actStats.total_alerts > 0 ? 'bg-amber-400' : 'bg-muted-foreground/30'} />
                </div>
              </div>

              {/* Activate Recent Projects */}
              <div className="bg-card border border-border rounded-sm">
                <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                  <h3 className="font-heading font-bold text-xs uppercase tracking-wider text-foreground/80">Recent Projects</h3>
                  <Link to="/closeout" className="text-xs text-blue-400 hover:text-blue-300 font-mono flex items-center gap-1 transition-colors" data-testid="view-all-projects">
                    View All <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
                <div className="divide-y divide-border/50">
                  {actStats.recent_projects?.map(proj => (
                    <Link key={proj.id} to={`/closeout/${proj.id}`} data-testid={`act-proj-${proj.id}`}
                      className="flex items-center justify-between px-5 py-3 hover:bg-secondary/30 transition-colors group">
                      <div className="flex items-center gap-4 min-w-0">
                        <ClipboardCheck className="w-4 h-4 text-muted-foreground shrink-0" />
                        <div className="min-w-0">
                          <p className="font-mono text-sm text-foreground truncate group-hover:text-primary transition-colors">
                            {proj.project_name}
                          </p>
                          <p className="text-[10px] font-mono text-muted-foreground mt-0.5">
                            {proj.asset_count} assets / {proj.division_count} divisions
                            {proj.has_floor_plan && ' / Floor plan linked'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded text-[10px] font-medium font-mono uppercase tracking-wide border ${actStatusColors[proj.status] || actStatusColors.draft}`}>
                          {proj.status}
                        </span>
                        <ArrowRight className="w-3.5 h-3.5 text-muted-foreground group-hover:text-primary transition-colors" />
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
